// ============================================================
// RE₿EL · Google Analytics 4 (GA4) tracking helper
// Measurement ID: G-3NNPQNNB27 (injected from index.html)
// ============================================================
// All exports are no-ops if window.gtag is not available
// (e.g. ad-blocker, SSR, or local dev with GA disabled).
// ------------------------------------------------------------

const GA_ID = "G-3NNPQNNB27";

const hasGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";

/**
 * Manual SPA page-view event. Call on every route change.
 * Includes page_title + page_location so GA4 receives a clean URL.
 */
export const trackPageView = (path) => {
  if (!hasGtag()) return;
  try {
    const page_path = path || (window.location.pathname + window.location.search);
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.origin + page_path,
      page_title: document.title,
      send_to: GA_ID,
    });
  } catch {}
};

/**
 * Generic event helper. Usage:
 *   trackEvent('cta_click', { cta_id: 'hero_explore_trendia' });
 */
export const trackEvent = (name, params = {}) => {
  if (!hasGtag()) return;
  try {
    window.gtag("event", name, params);
  } catch {}
};

/**
 * Identify a user (after login/register). GA4 user_id (string up to 256 chars).
 * No PII goes to GA4 — we hash the Mongo id; never send raw email.
 */
export const identifyUser = (userId, props = {}) => {
  if (!hasGtag() || !userId) return;
  try {
    window.gtag("set", "user_properties", {
      account_type: props.isPro ? "pro" : "free",
      ...(props.lang ? { language: props.lang } : {}),
    });
    window.gtag("config", GA_ID, { user_id: String(userId) });
  } catch {}
};

// ------------------------------------------------------------
// E-commerce / Conversion (Enhanced Ecommerce for GA4)
// ------------------------------------------------------------
export const trackBeginCheckout = ({ value = 39.90, plan = "pro_lifetime", feature = null } = {}) => {
  trackEvent("begin_checkout", {
    currency: "EUR",
    value,
    items: [
      {
        item_id: plan,
        item_name: "RE₿EL PRO Lifetime",
        item_category: "subscription",
        price: value,
        quantity: 1,
      },
    ],
    ...(feature ? { trigger_feature: feature } : {}),
  });
};

export const trackPurchase = ({ transactionId, value = 39.90, plan = "pro_lifetime" }) => {
  trackEvent("purchase", {
    transaction_id: transactionId,
    currency: "EUR",
    value,
    items: [
      {
        item_id: plan,
        item_name: "RE₿EL PRO Lifetime",
        item_category: "subscription",
        price: value,
        quantity: 1,
      },
    ],
  });
};

// ------------------------------------------------------------
// Auth events
// ------------------------------------------------------------
export const trackSignUp = (method = "email") =>
  trackEvent("sign_up", { method });

export const trackLogin = (method = "email") =>
  trackEvent("login", { method });

// ------------------------------------------------------------
// Engagement events (Academy, Mentor, Simulator)
// ------------------------------------------------------------
export const trackLessonView = (lessonKey, lessonTitle) =>
  trackEvent("lesson_view", { lesson_key: lessonKey, lesson_title: lessonTitle });

export const trackLessonComplete = ({ lessonKey, score, total, allCompleted }) =>
  trackEvent("lesson_complete", {
    lesson_key: lessonKey,
    score,
    total,
    completion_pct: total ? Math.round((score / total) * 100) : 0,
    all_completed: !!allCompleted,
  });

export const trackMentorMessage = ({ source = "academy", isPro = false, messageLength = 0 } = {}) =>
  trackEvent("ai_mentor_message", {
    source, // 'academy' | 'simulator'
    account_type: isPro ? "pro" : "free",
    message_length: messageLength,
  });

export const trackTtsPlay = ({ lessonKey, isPro = false } = {}) =>
  trackEvent("tts_play", {
    lesson_key: lessonKey,
    account_type: isPro ? "pro" : "free",
  });

export const trackSimulatorStart = ({ scenarioId, isPro = false } = {}) =>
  trackEvent("simulator_start", {
    scenario_id: scenarioId,
    account_type: isPro ? "pro" : "free",
  });

export const trackSimulatorEnd = ({ scenarioId, pnl, rank } = {}) =>
  trackEvent("simulator_end", {
    scenario_id: scenarioId,
    pnl,
    rank,
  });

// ------------------------------------------------------------
// Conversion funnel — premium unlock
// ------------------------------------------------------------
export const trackPremiumGateView = (feature) =>
  trackEvent("premium_gate_view", { feature });

export const trackPremiumUnlock = () =>
  trackEvent("premium_unlock", { event_category: "engagement" });

// ------------------------------------------------------------
// CTA / Button click tracking
// ------------------------------------------------------------
export const trackCta = (ctaId, params = {}) =>
  trackEvent("cta_click", { cta_id: ctaId, ...params });

// ------------------------------------------------------------
// Language change
// ------------------------------------------------------------
export const trackLanguageChange = (newLang) =>
  trackEvent("language_change", { new_language: newLang });
